import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from './supabaseConfig'

/**
 * One shared browser client.
 *
 * Only the publishable (anon) key is ever used here — never a service-role
 * key. All access is therefore subject to the project's RLS policies.
 */

const url = SUPABASE_URL
const key = SUPABASE_PUBLISHABLE_KEY

/** False only if the configuration is genuinely absent. */
export const supabaseConfigured = Boolean(url && key)

export const supabase: SupabaseClient | null = supabaseConfigured
  ? createClient(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    })
  : null

if (!supabaseConfigured) {
  console.error(
    '[supabase] client not created — URL or publishable key missing. Check lib/supabaseConfig.ts.',
  )
}

/**
 * Is there a session at all — staff or anonymous client?
 *
 * Asked before any query that RLS will refuse outright without one, so the
 * request is never sent. It reads the persisted session rather than creating
 * anything: use ensureAnonSession() to establish one.
 *
 * Added 29 Sep 2026. See the note on fetchProjects() in
 * utils/projectsSource.ts for why a refusal nobody sees still costs us.
 */
export async function hasSession(): Promise<boolean> {
  if (!supabase) return false
  try {
    const { data } = await supabase.auth.getSession()
    return Boolean(data.session?.user?.id)
  } catch (error) {
    console.warn('[supabase] auth unavailable:', error)
    return false
  }
}

/**
 * ONE sign-in at a time, shared by everyone who asks — 28 Sep 2026.
 *
 * Concurrent callers get the SAME promise, and therefore the same anonymous
 * user.
 *
 * **Why that matters more than it looks.** Several parts of a client page ask
 * for a session as they mount: the project load, the change requests, the
 * approvals, the transcripts. Each used to run its own getSession(), find
 * nothing, and call signInAnonymously(). Four sign-ups inside a hundred
 * milliseconds, four different users, and only the last one's tokens left in
 * storage. `join_project` would run as one of them and succeed, while the
 * browser kept another — a user who is a stranger to the project. RLS then
 * did exactly the right thing and returned NOTHING: not an error, not a 401,
 * an empty result with a 200 beside it. The page read that as a project it
 * could not find, and said so to the client.
 *
 * A race, so it came and went: the same link failed at 21:35 having worked
 * at 21:34 on the same machine. It is almost certainly what produced the
 * bursts of duplicate identities in the membership tables — three for one
 * client inside ten seconds on 12 September, which is a pattern no human
 * clicking could make.
 *
 * Never restore the old shape where each caller signs in for itself.
 */
let signingIn: Promise<string | null> | null = null

export function ensureAnonSession(): Promise<string | null> {
  if (!supabase) return Promise.resolve(null)
  if (signingIn) return signingIn

  const client = supabase
  signingIn = (async () => {
    try {
      const { data } = await client.auth.getSession()
      if (data.session?.user?.id) return data.session.user.id

      const { data: signedIn, error } = await client.auth.signInAnonymously()
      if (error) {
        console.warn('[supabase] anonymous sign-in failed:', error.message)
        return null
      }
      return signedIn.user?.id ?? null
    } catch (error) {
      console.warn('[supabase] auth unavailable:', error)
      return null
    } finally {
      /* Cleared only once this attempt has settled, so the next caller sees
         the session it established rather than starting a second one. */
      signingIn = null
    }
  })()

  return signingIn
}

/* ------------------------------------------- losing a client's session ---- */

/**
 * A client link, as opposed to the studio. Only these paths recover a lost
 * session below — a staff member signing out must stay signed out.
 */
function onClientLink(): boolean {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname
  return path.startsWith('/project/') || path.startsWith('/show/')
}

/**
 * Once per page load, so a session that cannot be re-established can never
 * put the page into a reload loop. sessionStorage throws in some privacy
 * modes, so every use of it is guarded.
 */
const RECOVERY_FLAG = 'portal.session.recovered'

function alreadyRecovered(): boolean {
  try {
    return sessionStorage.getItem(RECOVERY_FLAG) === '1'
  } catch {
    return false
  }
}

function markRecovered(): void {
  try {
    sessionStorage.setItem(RECOVERY_FLAG, '1')
  } catch {
    /* Private mode. The in-memory guard below still holds for this load. */
  }
}

let recovering = false

/**
 * A CLIENT WHOSE SESSION DISAPPEARS GETS IT BACK — 28 Sep 2026.
 *
 * The anonymous session is what makes a client link work at all: it carries
 * their name, and it is what RLS reads to decide they may see this project.
 * If it goes away mid-visit every request returns 401, the project cannot be
 * read, and the page tells the client their project does not exist. That is
 * exactly what a paying ATM client was shown when the staff-access check
 * signed them out — see claude/portal-os-client-signed-out-2026-09-28.md.
 *
 * That specific cause is fixed in utils/staffAuth.ts. This is the belt to
 * its braces, and it does not care WHY the session went — only that a client
 * must never be left at a dead end because of it.
 *
 * **Why a reload rather than a quiet re-sign-in.** A replacement anonymous
 * user is a different person to the database and holds no membership of this
 * project, so simply signing in again would still be refused. Reloading
 * re-runs the whole flow, and the access token in the link joins them again.
 *
 * Once per page load. A genuinely broken link must fail honestly rather than
 * flicker for ever, and the studio is excluded entirely.
 */
if (supabase) {
  supabase.auth.onAuthStateChange((event) => {
    if (event !== 'SIGNED_OUT') return
    if (!onClientLink()) return
    if (recovering || alreadyRecovered()) return

    recovering = true
    markRecovered()
    console.warn('[supabase] client session ended unexpectedly — re-establishing it')

    /* Let the current call stack unwind before navigating. */
    window.setTimeout(() => {
      window.location.reload()
    }, 0)
  })
}
